<template>
  <div
    v-if="flowMeters.length"
    class="woContainers"
  >
    <VBoxWithTitle
      v-for="(net) in flowMeters"
      :key="net.ref"
      containerClass="networkBox"
      closingIcon="mdi-chevron-up"
      collapsible
      openingIcon="mdi-chevron-down"
      class="flowMeterBox"
      :openedBox="net"
    >
      <template #header>
        <v-toolbar-title>
          {{ net.name }}
        </v-toolbar-title>
      </template>
      <template #content>
        <VTableRemote
          :key="refreshKey"
          ref="tableRemoteRef"
          :isSearchable="false"
          :showHeader="false"
          :items="net.meters"
          :headers="tableHeaders"
          :centerColumns="['dataSource', 'value', 'measureUnit', 'refresh', 'graphOnReportPdf']"
          :pagination="{}"
          :hideDefaultFooter="true"
          sortBy="treeOrder"
          class="blockTable"
        >
          <template
            v-slot:item="{ item }"
          >
            <tr>
              <td>
                <img
                  :src="`/assets/${item.flowMeterName ? 'FlowMeterHubBlack' : 'fuelMeterBlack'}.svg`"
                  :alt="item.flowMeterName ? 'Flow Meter' : 'Fuel Meter'"
                  width="24"
                  height="24"
                >
              </td>
              <td class="text-start w-name">
                {{ item.flowMeterName || item.fuelMeterName }}
              </td>
              <td class="text-start w-lname">
                {{ item.component.name }}
              </td>
              <td class="text-center w-sname">
                <div class="d-flex flex-column dataSource justify-center">
                  <img
                    class="dataSourceImg"
                    :src="`/assets/${dataSourceIcon[item.dataSource]}`"
                    :alt="item.dataSource"
                  >
                  <div class="dataSourceTitle">
                    {{ item.dataSource }}
                  </div>
                </div>
              </td>
              <td class="text-center pa-0">
                <div class="woSection w-value">
                  <Popover
                    :emptyTrigger="!item.value"
                    :top="popoverTops[item.id] || '60'"
                    :left="popoverLefts[item.id] || '0'"
                  >
                    <template v-slot:trigger="{ onClick }">
                      <div
                        class="woSectionData popover-trigger"
                        :data-meter-id="item.id"
                      >
                        <div
                          class="woValueSection w-value"
                        >
                          <v-icon
                            v-if="showAlertIcon(item)"
                            size="20"
                            color="red"
                            class="alert-animation"
                            @click="()=> openPopover(onClick, item)"
                          >
                            mdi-alert-outline
                          </v-icon>
                          <input
                            v-model="item.value"
                            type="number"
                            :class="{redAlert: showAlertIcon(item), woValue: true, manualInputStyle: true}"
                            :style="!item.value ? 'width: max-content' : ''"
                            :disabled="isCompletedOrLocked"
                            @change="manualInput(item)"
                          >
                          <span
                            v-if="item.measureId"
                            class="icon"
                          >
                            <v-btn
                              icon
                              color="primary"
                              xSmall
                              :disabled="isCompletedOrLocked"
                              @click="openCommentModal(item)"
                            >
                              <v-icon> {{ item.measureNote ? 'chat_bubble' : 'chat_bubble_outline' }}</v-icon>
                            </v-btn>
                          </span>
                        </div>
                        <div class="woBar" />
                        <div
                          class="woDate"
                        >
                          {{ item.lastReadingDate }}
                        </div>
                      </div>
                    </template>
                    <template v-slot:content="{ isClosed, close }">
                      <FlowMetersAlert
                        :isClosed="isClosed"
                        :meter="item"
                        :close="close"
                        :refreshReading="()=> refreshReading(item)"
                      />
                    </template>
                  </Popover>
                </div>
              </td>
              <td class="text-center pa-0 w-icon">
                <div class="woSection">
                  <Tooltip>
                    <template #trigger>
                      <v-icon
                        v-if="item.dataSource === SOURCE_CONTROLLER"
                        class="woReadingState"
                        :disabled="isCompletedOrLocked"
                        @click="()=> refreshReadingMesureAuto(item)"
                      >
                        mdi-refresh
                      </v-icon>
                    </template>
                    <template #content>
                      <div v-if="item.value">
                        {{ $t('devices.latestValueReading') }}
                      </div>
                      <div v-else>
                        {{ $t('devices.noValueReading') }}
                        {{ item.lastReadingDate }}
                      </div>
                    </template>
                  </Tooltip>
                </div>
              </td>
              <td class="text-center w-name">
                {{ $t(`volumes.${item.measureUnit}`) }}
              </td>
              <td class="text-center w-name">
                <v-tooltip top>
                  <template #activator="{ on, attrs }">
                    <v-icon
                      v-if="item.isPartOfMassBalance"
                      v-bind="attrs"
                      class="hover-icon"
                      v-on="on"
                    >
                      mdi-format-list-bulleted-square
                    </v-icon>
                  </template>
                  <div class="custom-tooltip">
                    <v-icon
                      style="color: white; padding-right: 25px"
                    >
                      mdi-information
                    </v-icon>
                    <div>
                      <p>{{ $t('dashboard.e4.itemRequired') }}</p>
                      <p style="font-weight: bold">
                        {{ $t('dashboard.e4.itemRequiredLastReadingValue', { lastReadingValue: item.previousMeasureValue }) }}
                      </p>
                    </div>
                  </div>
                </v-tooltip>
              </td>
              <td class="text-start w-name text--disabled underlined">
                {{ item.dailyVolumeConsumption }}
              </td>
              <td class="text-center w-name">
                <VCheckbox
                  disabled
                  :readonly="true"
                  :value="item.graphOnReportPdf"
                  class="d-flex justify-center"
                />
              </td>
            </tr>
          </template>
        </VTableRemote>
      </template>
    </VBoxWithTitle>
  </div>
  <div
    v-else
    class="title emptyWoContainers"
  >
    {{ $t('devices.emptyFlowMeters') }}
  </div>
</template>

<script>
import VBoxWithTitle from '@/components/shared/containers/VBoxWithTitle.vue';
import { mapGetters } from 'vuex';
import VTableRemote from '@/components/shared/content-views/VTableRemote.vue';
import VCheckbox from '@/components/shared/widgets/forms/VuetifyInputs/VCheckbox.vue';
import { SOURCE_CONTROLLER, SOURCE_MANUAL } from '@/aquassist-shared/constants/types/flowMeters';
import Popover from '@/components/popover/Popover.vue';
import Tooltip from '@/components/tooltip/Tooltip.vue';
import FlowMetersAlert from '@/views/workOrders/flowMeters/FlowMetersAlert/FlowMetersAlert.vue';
import moment from 'moment/moment';
import completionStatus from '@/constants/workOrderStatusChoices';
import AddAutomaticSentenceForm from '@/components/sites/visitSummaryComponents/AddAutomaticSentenceForm.vue';
import Vue from 'vue';


export default {
    name: 'WorkOrderFlowMeters',
    components: {
        FlowMetersAlert,
        Tooltip,
        Popover,
        VCheckbox,
        VTableRemote,
        VBoxWithTitle,
    },
    props: {
        flowmeterNetworks: {
            type: Array,
            default: () => [],
        },
        tables: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            activityId: null,
            refreshKey: 0,
            popoverTops: {},
            popoverLefts: {},
            flowMeters: [],
        };
    },

    computed: {
        ...mapGetters(['site', 'workOrder']),
        SOURCE_MANUAL() {
            return SOURCE_MANUAL;
        },
        SOURCE_CONTROLLER() {
            return SOURCE_CONTROLLER;
        },
        tableHeaders() {
            return [
                'type',
                'meterName',
                'component',
                'dataSource',
                'value',
                'refresh',
                'measureUnit',
                'massBalanceE4',
                'dailyVolumeConsumption',
                'graphOnReportPdf',
            ].map(header => ({
                text: header === 'refresh' || header === 'type' ? '' : this.$t(`devices.${header}`),
                value: header,
                sortable: false,
                class: this.getClassName(header),
            }));
        },
        dataSourceIcon() {
            return {
                MANUAL: 'manual_flow_meter.png',
                IOT: 'iot_flow_meter.png',
                CONTROLLER: 'controller_flow_meter.png',
            };
        },
        isCompletedOrLocked() {
            return this.workOrder.completionStatus === completionStatus.COMPLETED || !!this.workOrder?.lockData?.isLocked;
        },
    },
    watch: {
        flowmeterNetworks: {
            handler(newValue) {
                this.flowMeters = newValue.length ? newValue?.map(item => ({
                    ...item,
                    meters: item?.meters?.map(meter => ({
                        ...meter,
                        alertType: this.getAlertType(meter.value, meter.previousMeasureValue),
                    })),
                })) : [];
            },
            immediate: true,
        },
    },
    created() {
        this.activityId = this.workOrder.activities?.measuresAndSamplesCollect?.id;
    },
    methods: {
        getClassName(header) {
            let className = 'text-no-wrap';
            if (header === 'value') {
                className += ' d-width';
            }
            if (['dailyVolumeConsumption', 'graphOnReportPdf'].includes(header)) {
                className += ' text--disabled';
            }
            return className;
        },
        async refreshReading(item, autosave = false) {
            const payload = {
                id: item.analysisId,
                measuredAt: moment().toISOString(),
                value: item.value,
                team: this.workOrder.team.ref,
            };
            await this.$rq.addMeasureToAnalysis(this.workOrder.id, this.activityId, [payload]);
            if (!autosave) {
                await this.getMetersByNetwork();
            }
        },

        async refreshReadingMesureAuto(flowMeter) {
            // Step 1 - Get Last Measure
            const payload = {
                activityId: this.activityId,
                workOrder: {
                    id: this.workOrder.id,
                    site: this.workOrder.site.ref,
                },
                analysisTypeId: flowMeter.analysisType,
                componentId: flowMeter.component.id,
                analysisConfigId: flowMeter.analysisConfigId,
            };
            const { data } = await this.$rq.fetchLastMeasure(payload);
            // Step 2 - Edit Measure

            const newMeasure = {
                team: this.workOrder.team.ref,
                unit: flowMeter.unitsOfMeasure,
                id: flowMeter.analysisId,
                value: data.value,
                measuredAt: data.lastmodified,
            };
            const reponse = await this.$rq.addMeasureToAnalysis(this.workOrder.id, this.activityId, [newMeasure]);
            if (reponse.status === 200) {
                this.showMessage('success');
                await this.getMetersByNetwork();
                this.refreshKey += 1;
            } else {
                this.showMessage('error');
            }
        },

        getAlertType(newValue, oldValue) {
            if (newValue.length && (Number(newValue) > Number(10 * oldValue))) {
                return 'UPPER';
            }
            if (newValue.length && (Number(newValue) < Number(oldValue))) {
                return 'LOWER';
            }
            return '';
        },
        async manualInput(item) {
            // eslint-disable-next-line no-param-reassign
            item.alertType = this.getAlertType(item.value, item.previousMeasureValue);
            await this.refreshReading(item, true);
        },
        async openPopover(popoverClick, meter) {
            let y = '60';
            let x = '0';
            if (meter && meter.id) {
                const el = document.querySelector(`[data-meter-id="${ meter.id }"]`);
                if (el) {
                    const rect = el.getBoundingClientRect();
                    y = (rect.top + rect.height).toString();
                    x = rect.left.toString();
                }
                if (!this.popoverTops) this.popoverTops = {};
                if (!this.popoverLefts) this.popoverLefts = {};
                this.$set(this.popoverTops, meter.id, y);
                this.$set(this.popoverLefts, meter.id, x);
            }
            await popoverClick();
        },
        showAlertIcon(meter) {
            return meter.alertType !== undefined && meter.alertType !== '' && meter.value !== meter.measureValue;
        },
        async openCommentModal(item) {
            const payload = {
                note: item.measureNote || null,
            };
            this.$root.$emit('showModal', {
                title: this.$t('visitSummary.addAutomaticSentence'),
                component: Vue.component('addAutomaticSentenceForm', AddAutomaticSentenceForm),
                payload: { measure: payload || null, isAnalysis: true },
                onConfirm: async requestPayload => {
                    const reponse = await this.$rq.updateMeasureNote(
                        this.activityId,
                        this.tables[0].id,
                        item.measureId,
                        requestPayload,
                    );
                    if (reponse.status === 200) this.showMessage('success');
                    else this.showMessage('error');
                    this.getMetersByNetwork();
                },
            });
        },
        getComment(networkId, componentId) {
            // Trouver le bon network
            const goodNetwork = this.tables[0].networks.find(n => n.id === networkId);
            if (!goodNetwork) return null;

            // Trouver le bon component dans le bon network
            let component;
            // eslint-disable-next-line no-restricted-syntax
            for (const componentGroup of goodNetwork.components) {
                component = componentGroup.find(c => c.id === componentId);
                if (component) break;
            }
            if (!component) return null;

            return component;
        },

        hasComment(networkId, componentId) {
            return !!this.getComment(networkId, componentId);
        },

        getIconName(networkId, componentId) {
            return this.hasComment(networkId, componentId) ? 'chat_bubble' : 'chat_bubble_outline';
        },
        showMessage(type) {
            if (type === 'success') {
                this.$toasted.global.toast_success({
                    message: this.$t('msg.tableUpdated'),
                });
            }
            if (type === 'error') {
                this.$toasted.global.toast_error({
                    message: this.$t('msg.error'),
                });
            }
        },
        async getMetersByNetwork() {
            await this.$emit('getMetersByNetwork');
        },
    },
};
</script>

<style lang="scss" scoped>
  @import "./WorkOrderFlowMetersStyles.scss";

  .hover-icon {
    border: 1px solid black;
    border-radius: 4px;
    text-align: center;
    transition: color 0.3s;
  }

  .hover-icon:hover {
    color: white;
    background-color: #26384B;
  }

  .custom-tooltip {
    background-color: #26384B;
    color: white;
    border-radius: 4px;
    display: flex;
    flex-direction: row;
    height: 60px;
  }

  .v-tooltip__content {
    background-color: #26384B;

  }

  .alert-animation {
    animation: alertAnimation 0.9s ease-in-out infinite;
  }

  @keyframes alertAnimation {
    0% {
      transform: scale(1);
    }
    20% {
      transform: scale(1.15);
    }
    50% {
      transform: scale(0.85);
    }
    80% {
      transform: scale(1.15);
    }
    100% {
      transform: scale(1);
    }
  }
</style>
